import type { UseQueryResult } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { BaseReport } from 'services/assist-api/entities/reports/reports';
import type { BaseTest } from 'services/assist-api/entities/test/tests';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseTestReport = API_JSON & {
  test: BaseTest;
  report: BaseReport;
  minimumResponseThreshold: number;
  visible: boolean;
  allowAggregation: boolean;
  allowCluster: boolean;
  allowSystemWide: boolean;
  order: number;
};

const order = { 'order[order]': 'asc' } as const;
const groups = [
  'testsreports-test',
  'testsreports-report',
  'testsreports-minimumresponsethreshold',
  'testsreports-visible',
  'testsreports-allowaggregation',
  'testsreports-allowcluster',
  'testsreports-allowsystemwide',
  'report-details'
];
const include = 'test,report';
/**
 * Fetch tests-reports
 * @param params can be used to customise/limit the tests retrieved
 * @returns an array of tests
 */
export const getTestReports: GetMany<BaseTestReport> = async (params) =>
  (
    await ASSIST_API.get('tests-reports', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch tests-reports (singular)
 * @param params can be used to customise/limit the tests retrieved
 * @returns a single test
 */
export const getTestReport: GetOne<BaseTestReport> = async (id, params) =>
  (
    await ASSIST_API.get(`tests-reports/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Hook implementation to fetch tests-reports
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns tests-reports matching request
 */
export const useTestReports: AssistQuery.UseMany<BaseTestReport> = (params, options) =>
  useQueryAssist(['tests-reports', params], () => getTestReports(params), {
    staleTime: STALE_MEDIUM,
    ...options
  });

/**
 * Given an tests-reports id fetch and return the test-report
 *  - only runs once tests-reports Id is provided
 *  - fetches the one test-report
 *  - caches data
 * @param id tests-reports Id
 * @param params optional parameters to change requested data
 * @returns the matching test-report
 */
export const useTestReport: AssistQuery.UseOne<BaseTestReport> = (id, params, options) =>
  useQueryAssist(['tests-reports', { id, ...params }], () => getTestReport(id, params), {
    staleTime: STALE_MEDIUM,
    ...options,
    enabled: !!id
  });

export const useTestReportsByTest = (
  testId: number,
  params?: RequestParams
): UseQueryResult<RequestResponse<BaseTestReport[]>, unknown> =>
  useQueryAssist(
    ['tests-reports', { ...params, filters: { test: testId } }],
    () => getTestReports({ ...params, filters: { test: testId } }),
    { staleTime: STALE_LONG }
  );

export const useTestsReportByTestAndIdentifier = (
  testId: number,
  identifier: string,
  params?: RequestParams
): BaseTestReport | undefined => {
  const reports = useTestReportsByTest(testId, params);
  return reports.data?.json.find((tr) => tr.report.identifier === identifier);
};
